import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Assert } from '@team/assert';
import { NumberKit } from '@team/number';
import { ReactHooks } from '@team/react-hooks';
import { Dimension, Props } from './types';
import { Mode, State } from './enums';
import { INITIAL_DIMENSION } from './const';
import {
  buildInnerClassName,
  buildInnerStyle,
  buildOuterStyle,
  getDimention,
  getScrollTarget,
  getStateByStickyFlag,
  isShouldSticky,
} from './model';

export const Entry: FC<Props> = ({
  mode = Mode.TOP,
  scrollContainer,
  offset = 0,
  children,
  className,
  stickyClassName,
  stickyStyle,
  stickyFixedClassName,
  stickyFixedStyle,
  stickyReleasedClassName,
  stickyReleasedStyle,
}) => {
  const stickyRef = useRef<HTMLDivElement>(null);

  const [state, setState] = useState<State>(State.ORIGINAL);
  const frameId = useRef(0);

  const stickyDimentionRef = useRef<Dimension>(INITIAL_DIMENSION);

  const handleScroll = useCallback(() => {
    frameId.current = 0;

    if (Assert.isNull(stickyRef.current)) {
      return;
    }

    const scrollableElement = getScrollTarget(scrollContainer);
    const scrollDimention = getDimention(scrollableElement);
    const stickyDimention = getDimention(stickyRef.current);

    const shouldSticky = isShouldSticky(
      mode,
      stickyDimention,
      scrollDimention,
      offset
    );
    const nextState = getStateByStickyFlag(shouldSticky);

    stickyDimentionRef.current = stickyDimention;

    setState(nextState);
  }, [stickyRef, scrollContainer, mode, offset]);

  const debouncedScroll = useCallback(() => {
    if (NumberKit.isZero(frameId.current)) {
      const nextFrameId = requestAnimationFrame(handleScroll);

      frameId.current = nextFrameId;
    }
  }, [handleScroll]);

  ReactHooks.useMountEffect(handleScroll);

  useEffect(() => {
    if (Assert.isNull(stickyRef)) {
      return;
    }

    const scrollableElement = getScrollTarget(scrollContainer);

    scrollableElement.addEventListener('scroll', debouncedScroll, {
      passive: true,
    });

    return () => {
      scrollableElement.removeEventListener('scroll', debouncedScroll);

      if (!NumberKit.isZero(frameId.current)) {
        cancelAnimationFrame(frameId.current);
      }
    };
  }, [scrollContainer, stickyRef, debouncedScroll]);

  const innerClassNames = buildInnerClassName(
    state,
    stickyClassName,
    stickyFixedClassName,
    stickyReleasedClassName
  );

  const outerStyle = buildOuterStyle(state, stickyDimentionRef.current);
  const innerStyle = buildInnerStyle(
    mode,
    state,
    stickyDimentionRef.current,
    offset,
    stickyStyle,
    stickyFixedStyle,
    stickyReleasedStyle
  );

  return (
    <div className={className} ref={stickyRef} style={outerStyle}>
      <div className={innerClassNames} style={innerStyle}>
        {children}
      </div>
    </div>
  );
};
